import { ConnectionState, LocalAudioTrack, LocalVideoTrack, Room, Track } from 'livekit-client'
import type { Participant } from 'livekit-client'
import type { NativeMicSource } from './nativeMic'

type RoomEventName = Parameters<Room['on']>[0]
type RoomListener = { event: RoomEventName; handler: (...args: any[]) => void }
type ParticipantEventName = Parameters<Participant['on']>[0]
type ParticipantListener = { participant: Participant; event: ParticipantEventName; handler: (...args: any[]) => void }

/**
 * Web Audio graph used to apply a mic-volume multiplier when no RNNoise
 * processor is active. Has exactly one owner (`CallSession`) so it can never
 * leak nodes across a mic-device switch or a repeated volume change.
 */
export class MicGainChain {
  constructor(
    readonly sourceTrackId: string,
    private readonly sourceNode: MediaStreamAudioSourceNode,
    readonly gainNode: GainNode,
    private readonly destination: MediaStreamAudioDestinationNode,
    readonly outputTrack: MediaStreamTrack,
  ) {}

  dispose() {
    try { this.sourceNode.disconnect() } catch { /* already disconnected */ }
    try { this.gainNode.disconnect() } catch { /* already disconnected */ }
    this.outputTrack.stop()
  }
}

/**
 * Sole owner of every resource belonging to one voice-channel connection:
 * the `Room`, its own Room listeners, deferred-sync timers, custom
 * screen-share tracks/stream, the mic gain chain and (macOS) the native mic source. `LiveKitManager`
 * creates exactly one `CallSession` per `connect()` and never mutates a
 * session's resources from outside it. `dispose()` tears everything down in
 * a fixed order and is safe to call more than once.
 */
export class CallSession {
  readonly room: Room
  readonly channelId: string
  readonly generation: number
  /** This channel's mic audio bitrate (kbps, 8-128) — see `manager.ts`'s `audioPresetForChannel`. */
  readonly audioBitrateKbps: number
  closed = false

  private listeners: RoomListener[] = []
  private participantListeners: ParticipantListener[] = []
  private timers = new Set<ReturnType<typeof setTimeout>>()
  private screenTracks: Array<LocalVideoTrack | LocalAudioTrack> = []
  private screenStream: MediaStream | null = null
  private screenEndedTrack: MediaStreamTrack | null = null
  private screenEndedHandler: (() => void) | null = null
  private micGainChain: MicGainChain | null = null
  private nativeMic: NativeMicSource | null = null
  private disposePromise: Promise<void> | null = null

  constructor(room: Room, channelId: string, generation: number, audioBitrateKbps: number) {
    this.room = room
    this.channelId = channelId
    this.generation = generation
    this.audioBitrateKbps = audioBitrateKbps
  }

  /** Registers a Room listener owned by this session, so `dispose()` can remove it individually. */
  listen(event: RoomEventName, handler: (...args: any[]) => void) {
    this.room.on(event as any, handler as any)
    this.listeners.push({ event, handler })
  }

  /**
   * Registers a listener directly on a `Participant` instance (e.g. `IsSpeakingChanged`, which
   * only fires per-participant, never on `Room`). Tracked the same way as Room listeners so
   * `dispose()` removes it too — otherwise the closure would keep the participant reachable
   * past its `ParticipantDisconnected`.
   */
  listenParticipant(participant: Participant, event: ParticipantEventName, handler: (...args: any[]) => void) {
    participant.on(event as any, handler as any)
    this.participantListeners.push({ participant, event, handler })
  }

  /** Removes every listener previously registered for this one participant (e.g. on disconnect). */
  unlistenParticipant(participant: Participant) {
    this.participantListeners = this.participantListeners.filter(({ participant: p, event, handler }) => {
      if (p !== participant) return true
      p.off(event as any, handler as any)
      return false
    })
  }

  /** Schedules a callback that self-cancels once the session is disposed. */
  setTimer(callback: () => void, delayMs: number) {
    const timer = setTimeout(() => {
      this.timers.delete(timer)
      if (!this.closed) callback()
    }, delayMs)
    this.timers.add(timer)
  }

  /**
   * Hands ownership of a live-capture `MediaStream` + its published tracks to
   * this session. Releases any previous custom share's "ended" listener first
   * so switching sources never leaks one.
   */
  adoptScreenShare(stream: MediaStream, tracks: Array<LocalVideoTrack | LocalAudioTrack>, onEnded: () => void) {
    this.releaseScreenReferences()
    this.screenStream = stream
    this.screenTracks = tracks
    this.screenEndedTrack = stream.getVideoTracks()[0] ?? null
    this.screenEndedHandler = onEnded
    this.screenEndedTrack?.addEventListener('ended', onEnded, { once: true })
  }

  hasCustomScreenShare() {
    return this.screenTracks.length > 0
  }

  /** Unpublishes and stops the custom screen-share tracks and their source `MediaStream`. */
  async stopScreenShare() {
    const tracks = this.screenTracks.splice(0)
    const stream = this.screenStream
    this.releaseScreenReferences()
    for (const track of tracks) {
      await this.room.localParticipant.unpublishTrack(track, false).catch(() => undefined)
      track.stop()
    }
    stream?.getTracks().forEach(track => track.stop())
  }

  getMicGainChain(): MicGainChain | null {
    return this.micGainChain
  }

  /** Installs a new mic gain chain, disposing whatever chain preceded it. */
  setMicGainChain(chain: MicGainChain) {
    this.disposeMicGainChain()
    this.micGainChain = chain
  }

  disposeMicGainChain() {
    this.micGainChain?.dispose()
    this.micGainChain = null
  }

  getNativeMic(): NativeMicSource | null {
    return this.nativeMic
  }

  /**
   * Takes ownership of a macOS native mic source (see `nativeMic.ts`), stopping any previous one.
   * Returns false — and stops the source itself — if this session was disposed while it was
   * starting, since `dispose()` has already run and would never release it.
   */
  adoptNativeMic(source: NativeMicSource): boolean {
    if (this.closed) {
      void source.stop()
      return false
    }
    if (this.nativeMic !== source) void this.nativeMic?.stop()
    this.nativeMic = source
    return true
  }

  /** Idempotent teardown — concurrent/repeated calls all resolve the same underlying promise. */
  dispose(): Promise<void> {
    if (!this.disposePromise) this.disposePromise = this.disposeInternal()
    return this.disposePromise
  }

  private releaseScreenReferences() {
    if (this.screenEndedTrack && this.screenEndedHandler) {
      this.screenEndedTrack.removeEventListener('ended', this.screenEndedHandler)
    }
    this.screenEndedTrack = null
    this.screenEndedHandler = null
    this.screenStream = null
  }

  /**
   * Fixed teardown order (do not reorder — see VOICE_CALLS_ARCHITECTURE.md):
   * 1. mark closed so any in-flight callback sees it immediately;
   * 2. cancel timers;
   * 3. remove only this session's own Room and Participant listeners (foreign/UI listeners are untouched);
   * 4. unpublish + stop custom screen-share tracks/stream;
   * 5. stop the mic's RNNoise processor;
   * 6. dispose the mic gain chain;
   * 7. stop the macOS native mic capture, if any;
   * 8. disconnect the Room if it isn't already.
   * Each step's failure is swallowed so it never blocks the remaining ones.
   */
  private async disposeInternal() {
    this.closed = true

    for (const timer of this.timers) clearTimeout(timer)
    this.timers.clear()

    for (const { event, handler } of this.listeners) this.room.off(event as any, handler as any)
    this.listeners = []

    for (const { participant, event, handler } of this.participantListeners) participant.off(event as any, handler as any)
    this.participantListeners = []

    await this.stopScreenShare().catch(() => undefined)

    const microphone = this.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
    if (microphone instanceof LocalAudioTrack) {
      await microphone.stopProcessor().catch(() => undefined)
    }
    this.disposeMicGainChain()

    await this.nativeMic?.stop().catch(() => undefined)
    this.nativeMic = null

    if (this.room.state !== ConnectionState.Disconnected) {
      await this.room.disconnect().catch(() => undefined)
    }
  }
}
